import { WorkflowNodeType } from '../../models/workflow-builder.model';
import { WorkflowGraphNode } from './flow-graph.model';

/**
 * Metadatos de presentación por TIPO de nodo (TRIGGER/CONDITION/ACTION/WAIT/END/ANNOTATION).
 *
 * Equivalente a `NODE_TYPE_META` del builder de chatflow. Los SUBtipos concretos y sus
 * campos de config NO se hardcodean aquí: los provee el backend en
 * `WorkflowNodeCatalogResponse` (label, description, category, defaultConfig). Este mapa
 * solo cubre lo que es estable por tipo (color, icono, etiqueta del tipo), igual que en
 * chatflow.
 */
export interface WorkflowNodeTypeMeta {
  type: WorkflowNodeType;
  label: string;
  description: string;
  icon: string;
  /**
   * Clases Tailwind COMPLETAS y estáticas — Tailwind v4 solo detecta literales.
   * PROHIBIDO construirlas concatenando (el builder viejo generaba
   * `getNodeHeaderClass` por switch, aquí se centraliza).
   */
  iconClass: string;
  accentClass: string;
  ringClass: string;
}

export const WORKFLOW_NODE_TYPE_META: Record<WorkflowNodeType, WorkflowNodeTypeMeta> = {
  TRIGGER: {
    type: 'TRIGGER',
    label: 'Disparador',
    description: 'Inscribe registros que cumplen el criterio.',
    icon: 'fa-solid fa-bolt',
    iconClass: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
    accentClass: 'border-l-blue-500',
    ringClass: 'ring-blue-500'
  },
  ACTION: {
    type: 'ACTION',
    label: 'Acción',
    description: 'Ejecuta una acción sobre el registro.',
    icon: 'fa-solid fa-play',
    iconClass: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
    accentClass: 'border-l-emerald-500',
    ringClass: 'ring-emerald-500'
  },
  CONDITION: {
    type: 'CONDITION',
    label: 'Condición',
    description: 'Divide el flujo según una condición.',
    icon: 'fa-solid fa-code-branch',
    iconClass: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
    accentClass: 'border-l-amber-500',
    ringClass: 'ring-amber-500'
  },
  WAIT: {
    type: 'WAIT',
    label: 'Espera',
    description: 'Pausa el flujo hasta cumplir una condición o tiempo.',
    icon: 'fa-solid fa-clock',
    iconClass: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400',
    accentClass: 'border-l-violet-500',
    ringClass: 'ring-violet-500'
  },
  END: {
    type: 'END',
    label: 'Fin',
    description: 'Termina el recorrido del registro.',
    icon: 'fa-solid fa-flag-checkered',
    iconClass: 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400',
    accentClass: 'border-l-rose-500',
    ringClass: 'ring-rose-500'
  },
  ANNOTATION: {
    type: 'ANNOTATION',
    label: 'Nota',
    description: 'Anotación visual sin efecto en la ejecución.',
    icon: 'fa-solid fa-note-sticky',
    iconClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
    accentClass: 'border-l-yellow-400',
    ringClass: 'ring-yellow-400'
  }
};

export function getWorkflowNodeTypeMeta(nodeType: WorkflowNodeType): WorkflowNodeTypeMeta {
  return WORKFLOW_NODE_TYPE_META[nodeType];
}

/**
 * Categoría funcional de un SUBtipo de acción (mapa §4 del análisis). Se usa para agrupar
 * la paleta y elegir el componente de config del inspector en fases posteriores. El
 * catálogo del backend ya trae `category`; este mapa es el respaldo/normalización cuando
 * se razona solo con el subtipo (p. ej. al construir un edge o el resumen de la tarjeta).
 */
export type WorkflowActionCategory =
  | 'Comunicación'
  | 'Tarea y actividad'
  | 'Pipeline de ventas'
  | 'Tickets'
  | 'Webhook'
  | 'Integración y código'
  | 'CRM';

export const WORKFLOW_ACTION_SUBTYPE_CATEGORY: Record<string, WorkflowActionCategory> = {
  // Comunicación
  SEND_MARKETING_EMAIL: 'Comunicación',
  SEND_ONE_TO_ONE_EMAIL: 'Comunicación',
  SEND_INTERNAL_NOTIFICATION: 'Comunicación',
  SEND_TEAMS_NOTIFICATION: 'Comunicación',
  SEND_SLACK_NOTIFICATION: 'Comunicación',
  SEND_SMS: 'Comunicación',
  SEND_WHATSAPP: 'Comunicación',
  // Tarea y actividad
  CREATE_TASK: 'Tarea y actividad',
  CREATE_NOTE: 'Tarea y actividad',
  COMPLETE_TASK: 'Tarea y actividad',
  SCHEDULE_MEETING: 'Tarea y actividad',
  // Pipeline de ventas
  CHANGE_DEAL_STAGE: 'Pipeline de ventas',
  UPDATE_DEAL_CLOSE_DATE: 'Pipeline de ventas',
  CHANGE_PIPELINE: 'Pipeline de ventas',
  // Tickets — categoría propia (la misma que devuelve el catálogo del backend). Antes caían en
  // "Pipeline de ventas", que abría el panel de negocios y no tenía ni un campo de ticket.
  CREATE_TICKET: 'Tickets',
  CHANGE_TICKET_STATUS: 'Tickets',
  CHANGE_TICKET_PRIORITY: 'Tickets',
  CHANGE_TICKET_PIPELINE: 'Tickets',
  ASSIGN_TICKET: 'Tickets',
  // Webhook
  SEND_WEBHOOK: 'Webhook',
  // Integración y código
  NATIVE_INTEGRATION_ACTION: 'Integración y código',
  CODE_SNIPPET: 'Integración y código'
};

/** Categoría de un subtipo de ACTION; el resto de mutaciones de registro caen en CRM. */
export function getWorkflowActionCategory(nodeSubtype: string | null | undefined): WorkflowActionCategory {
  if (!nodeSubtype) {
    return 'CRM';
  }
  return WORKFLOW_ACTION_SUBTYPE_CATEGORY[nodeSubtype] ?? 'CRM';
}

// ── Puertos de salida (PUERTO = branchKey) ────────────────────────────────────

/** Id del ÚNICO puerto de entrada de una tarjeta (todas las aristas entran por aquí). */
export const TARGET_HANDLE_ID = 'in';

/**
 * Puerto genérico del que cuelgan las aristas cuyo `branchKey` ya no corresponde a ningún
 * puerto de salida real del nodo (p. ej. una rama personalizada de MULTI_BRANCH que se
 * quitó del config). Se dibujan aquí para poder verlas y borrarlas a mano, en vez de que
 * desaparezcan — así el guardado REPLACE nunca pierde una rama sin que el usuario lo decida.
 */
export const ORPHAN_HANDLE_ID = '__orphan';

const DOT_NEUTRAL = 'bg-gray-400 dark:bg-gray-500';
const DOT_POSITIVE = 'bg-emerald-500';
const DOT_NEGATIVE = 'bg-rose-500';
const DOT_WARNING = 'bg-amber-500';

/**
 * Puerto de salida de una tarjeta de nodo. EL PUERTO DETERMINA EL `branchKey` de la
 * arista que nazca de él: el usuario nunca elige un branchKey en un `<select>` (el builder
 * viejo lo pedía en un modal), arrastra desde "Sí" y eso YA es branchKey='YES'.
 */
export interface WorkflowBranchHandle {
  /** branchKey exacto que viajará en el WorkflowEdge. */
  branchKey: string;
  label: string;
  description: string;
  /** Clase estática del punto del puerto (color por semántica de la rama). */
  dotClass: string;
}

/** Descripción legible por tipo de rama (portado de `branchKeyDescription` del builder viejo). */
export function branchKeyDescription(key: string): string {
  switch (key) {
    case 'YES':
      return 'Sí — cuando la condición se cumple';
    case 'NO':
      return 'No — cuando la condición no se cumple';
    case 'DEFAULT':
      return 'Camino por defecto';
    case 'TIMEOUT':
      return 'Al vencer la espera sin cumplirse la condición';
    case 'EXIT':
      return 'Termina el workflow para este registro';
    case 'FALLBACK':
      return 'Si ninguna otra rama aplica';
    default:
      return `Rama "${key}"`;
  }
}

/**
 * Etiqueta legible de una rama canónica. INC_CRM_020 (8) inc44/inc46: sin esto, los puertos
 * `TIMEOUT`/`EXIT`/`FALLBACK` se pintaban con su clave cruda tanto en la tarjeta del lienzo como
 * en el inspector. Las ramas que nombra el usuario (MULTI_BRANCH, AB_SPLIT) caen al `default` y
 * se muestran tal cual las escribió.
 */
export function branchKeyLabel(key: string): string {
  switch (key) {
    case 'YES':
      return 'Sí';
    case 'NO':
      return 'No';
    case 'DEFAULT':
      return 'Por defecto';
    case 'TIMEOUT':
      return 'Al vencer el plazo';
    case 'EXIT':
      return 'Salir del workflow';
    case 'FALLBACK':
      return 'Si ninguna otra aplica';
    default:
      return key;
  }
}

function branchDotClass(key: string): string {
  switch (key) {
    case 'YES':
      return DOT_POSITIVE;
    case 'NO':
      return DOT_NEGATIVE;
    case 'TIMEOUT':
    case 'EXIT':
    case 'FALLBACK':
      return DOT_WARNING;
    default:
      return DOT_NEUTRAL;
  }
}

function handle(key: string, label?: string): WorkflowBranchHandle {
  return { branchKey: key, label: label ?? branchKeyLabel(key), description: branchKeyDescription(key), dotClass: branchDotClass(key) };
}

function toBranchKeyList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => (item && typeof item === 'object' ? (item as Record<string, unknown>)['branchKey'] : null))
    .filter((key): key is string => typeof key === 'string' && !!key.trim());
}

function configString(node: WorkflowGraphNode, key: string, fallback: string): string {
  const raw = node.config[key];
  return typeof raw === 'string' && raw.trim() ? raw : fallback;
}

/**
 * Puertos de salida de un nodo. El puerto determina el `branchKey` de la arista que nazca
 * de él. Portado 1:1 de `WorkflowBuilderComponent.availableBranchKeys`, que refleja el
 * contrato REAL del backend (el análisis §3 dice "MATCH/NO_MATCH" de forma laxa; las
 * ramas reales son YES/NO/DEFAULT, success/timeout, etc.).
 */
export function getOutputBranchHandles(node: WorkflowGraphNode): WorkflowBranchHandle[] {
  const key = `${node.nodeType}/${node.nodeSubtype ?? ''}`;
  switch (key) {
    case 'CONDITION/IF_ELSE':
    case 'CONDITION/PROPERTY_CONDITION':
    case 'CONDITION/SEGMENT_MEMBERSHIP_CONDITION':
    case 'CONDITION/ASSOCIATED_RECORD_CONDITION':
    case 'CONDITION/ACTIVITY_HISTORY_CONDITION':
      return [handle('YES', 'Sí'), handle('NO', 'No'), handle('DEFAULT', 'Por defecto')];

    case 'CONDITION/MULTI_BRANCH': {
      const branches = toBranchKeyList(node.config['branches']);
      const fallback = configString(node, 'fallbackBranchKey', 'DEFAULT');
      return branches.length ? [...branches, fallback].map((k) => handle(k)) : [handle('DEFAULT', 'Por defecto')];
    }

    case 'CONDITION/AB_SPLIT': {
      const branches = toBranchKeyList(node.config['branches']);
      return (branches.length ? branches : ['A', 'B']).map((k) => handle(k));
    }

    case 'WAIT/UNTIL_CONDITION': {
      const success = configString(node, 'successBranchKey', 'DEFAULT');
      const timeoutCfg = node.config['timeout'];
      const timeout =
        timeoutCfg && typeof timeoutCfg === 'object'
          ? String((timeoutCfg as Record<string, unknown>)['branchKey'] ?? 'TIMEOUT')
          : 'TIMEOUT';
      return dedupeHandles([handle(success, 'Cumple'), handle(timeout)]);
    }

    case 'WAIT/EXIT_CONDITION': {
      const cont = configString(node, 'continueBranchKey', 'DEFAULT');
      const exit = configString(node, 'exitBranchKey', 'EXIT');
      return dedupeHandles([handle(cont, 'Continúa'), handle(exit)]);
    }

    case 'END/':
    case 'ANNOTATION/':
      return [];

    default:
      // TRIGGER, ACTION (todos los subtipos), WAIT por tiempo → una única salida DEFAULT.
      if (node.nodeType === 'END' || node.nodeType === 'ANNOTATION') {
        return [];
      }
      return [handle('DEFAULT', 'Siguiente')];
  }
}

function dedupeHandles(handles: WorkflowBranchHandle[]): WorkflowBranchHandle[] {
  const seen = new Set<string>();
  const result: WorkflowBranchHandle[] = [];
  for (const h of handles) {
    if (!seen.has(h.branchKey)) {
      seen.add(h.branchKey);
      result.push(h);
    }
  }
  return result;
}
